// Segmented period-range toggle for the KPI Overview header. Follows the
// ARIA tablist pattern: arrow keys move selection (wrapping), Home/End
// jump to the ends. `disabled` blocks both click and keyboard changes.
//
// The labels name calendar months because that is what the panel now
// windows on — "30d" claimed a rolling 30-day window the compliance figures
// never had. The stored values are unchanged so deep links and saved state
// keep working; only what the reader sees moved.

import type { JSX, KeyboardEvent } from "react";

export type PeriodRangeValue = "30d" | "90d" | "12m" | "ytd";

interface RangeOption {
  value: PeriodRangeValue;
  /** Terse visible label — the toggle sits beside the panel's primary
   *  action and has to stay narrow. */
  label: string;
  /** Spoken expansion. The accessible name is prefixed with the visible
   *  label so it contains it (WCAG 2.5.3 Label in Name) and speech input can
   *  still say "3m", while a screen-reader user hears the actual window. */
  spoken: string;
}

const OPTIONS: readonly RangeOption[] = [
  { value: "30d", label: "1m", spoken: "last 1 month" },
  { value: "90d", label: "3m", spoken: "last 3 months" },
  { value: "12m", label: "12m", spoken: "last 12 months" },
  { value: "ytd", label: "YTD", spoken: "year to date" },
];

export interface PeriodRangeToggleProps {
  value: PeriodRangeValue;
  onChange: (v: PeriodRangeValue) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

// Brand SegmentedControl visual: raised pill on a slate-tinted track,
// not the prior side-by-side dividers-with-active-fill. The selected
// option gets a card-style elevation (white / slate-900 surface with
// shadow-sm) so the selection is colour-blind-safe (visible via
// elevation, not just colour).
function buttonClasses(active: boolean, disabled: boolean): string {
  const base =
    "px-3 py-1 text-caption rounded transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1";
  const disabledClass = disabled ? "opacity-50 cursor-not-allowed" : "";
  if (active) {
    return `${base} ${disabledClass} bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-medium shadow-sm`;
  }
  return `${base} ${disabledClass} bg-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200`;
}

export default function PeriodRangeToggle({
  value,
  onChange,
  disabled = false,
  ariaLabel = "Period range",
}: PeriodRangeToggleProps): JSX.Element {
  const activeIdx = OPTIONS.findIndex((o) => o.value === value);

  const selectByIndex = (idx: number) => {
    if (disabled) return;
    const wrapped = ((idx % OPTIONS.length) + OPTIONS.length) % OPTIONS.length;
    const target = OPTIONS[wrapped];
    if (target.value !== value) {
      onChange(target.value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        selectByIndex(activeIdx + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        selectByIndex(activeIdx - 1);
        break;
      case "Home":
        e.preventDefault();
        selectByIndex(0);
        break;
      case "End":
        e.preventDefault();
        selectByIndex(OPTIONS.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex gap-0 rounded-md bg-slate-100 dark:bg-slate-700 p-0.5"
    >
      {OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            className={buttonClasses(active, disabled)}
            onClick={() => {
              if (disabled || active) return;
              onChange(opt.value);
            }}
            onKeyDown={handleKeyDown}
            aria-label={`${opt.label}, ${opt.spoken}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
